import { useMutationHook, useQueryHook } from "@/hooks"
import {
  createActivity,
  createActivityAnalyze,
  createActivityData,
  deleteActivity,
  deleteActivityData,
  readActivitiesAnalysisStatus,
  readActivity,
  readActivityData,
  readActivityDetail,
  readActivityList,
  readActivityOverviewList,
  readFilteredActivities,
  readActivityParticipatedPlayerList,
  readActivityTypeStats,
  readActivityTypeStatsOfPlayer,
  readAnalyzingActivity,
  readLatestActivity,
  readPlayersRpe,
  readRecentActivityOverview,
  readSessionList,
  readActivitySearchList,
  upsertActivity,
  upsertActivityTime,
  upsertPlayersRpe,
  upsertReAnalyzeActivity,
  readActivityPlayerDeviceList,
  type ReadFilteredActivitiesParams,
  type FilteredActivityItem,
} from "@/app/api/activity"
import {
  Activity,
  ReadActivitiesAnalysisStatusParams,
  ReadActivitiesAnalysisStatusResponse,
  ReadActivityDetailResponse,
  ReadActivityListParams,
  ReadActivityOverviewListParams,
  ReadActivityParticipatedPlayerListParams,
  ReadActivityParticipatedPlayerListResponse,
  ReadActivityTypeStatsOfPlayerParams,
  ReadAnalyzingActivityResponse,
  ReadLatestActivityParams,
  ReadRecentActivityOverviewParams,
  ReadSessionListParams,
  ReadActivityDataResponse,
  ReadPlayerRpeResponse,
} from "@/types/activity"
import { ApiError } from "@/app/api/type"
import { UseQueryOptions } from "@tanstack/react-query"
import { Session } from "@/types/session"

export const useActivityListQuery = (params: ReadActivityListParams) => {
  return useQueryHook({
    queryKey: ["readActivityList", ...Object.values(params || {})],
    queryFn: () => readActivityList(params),
  })
}

export const useActivityQuery = (activityId?: number) => {
  return useQueryHook({
    queryKey: ["readActivity", activityId],
    queryFn: () => readActivity(activityId as number),
    options: {
      enabled: !!activityId,
    } as UseQueryOptions<Activity, ApiError>,
  })
}

export const useSearchActivityQuery = (keyword: string) => {
  return useQueryHook({
    queryKey: ["readActivitySearchList", keyword],
    queryFn: () => readActivitySearchList(keyword),
  })
}

export const useActivityOverviewListQuery = (params: ReadActivityOverviewListParams) => {
  return useQueryHook({
    queryKey: ["readActivityOverviewList", ...Object.values(params)],
    queryFn: () => readActivityOverviewList(params),
  })
}

/** 경량 필터 기반 액티비티 목록 (캘린더 위젯 등).
 * staleTime(CSV Export): 그 시간 안에는 같은 조건을 다시 부르지 않고 캐시 사용.
 * 전역 기본이 gcTime 0(즉시 GC)이라, 필터 토글로 쿼리 키가 바뀌는 순간 이전 조건의 캐시가
 * 삭제돼 staleTime 이 무력화된다 — 왕복 재사용이 목적이므로 gcTime 도 함께 유지.
 * 캐시 사용 시 조건 변경 순간에도 직전 목록을 유지해 표가 비었다 채워지는 깜빡임을 막는다. */
export const useFilteredActivitiesQuery = (
  params: ReadFilteredActivitiesParams,
  staleTime?: number,
) => {
  return useQueryHook<FilteredActivityItem[], unknown>({
    queryKey: ["readFilteredActivities", ...Object.values(params)],
    queryFn: () => readFilteredActivities(params),
    options:
      staleTime !== undefined
        ? {
            staleTime,
            gcTime: Math.max(staleTime, 5 * 60_000),
            placeholderData: (prev: FilteredActivityItem[] | undefined) => prev,
          }
        : undefined,
  })
}

export const useRecentActivityOverviewQuery = (params: ReadRecentActivityOverviewParams) => {
  return useQueryHook({
    queryKey: ["readRecentActivityOverview", ...Object.values(params)],
    queryFn: () => readRecentActivityOverview(params),
  })
}

export const useLatestActivityQuery = (params: ReadLatestActivityParams, enabled = true) => {
  return useQueryHook({
    queryKey: ["readLatestActivity", ...Object.values(params)],
    queryFn: () => readLatestActivity(params),
    options: {
      enabled,
    },
  })
}

export const useActivityDataQuery = (activityId?: number) => {
  return useQueryHook({
    queryKey: ["readActivityData", activityId],
    queryFn: () => readActivityData(activityId as number),
    options: {
      enabled: !!activityId,
    } as UseQueryOptions<ReadActivityDataResponse, ApiError>,
  })
}

export const useActivityTypeStatsQuery = () => {
  return useQueryHook({
    queryKey: ["readActivityTypeStats"],
    queryFn: () => readActivityTypeStats(),
  })
}

export const useActivityTypeStatsOfPlayerQuery = (params: ReadActivityTypeStatsOfPlayerParams) => {
  return useQueryHook({
    queryKey: ["readActivityTypeStatsOfPlayer", ...Object.values(params)],
    queryFn: () => readActivityTypeStatsOfPlayer(params),
  })
}

export const useAnalyzingActivityQuery = (activityId?: number, enabled: boolean = true) => {
  return useQueryHook({
    queryKey: ["readAnalyzingActivity", activityId],
    queryFn: () => readAnalyzingActivity(activityId as number),
    options: {
      enabled: enabled && !!activityId,
    } as UseQueryOptions<ReadAnalyzingActivityResponse, ApiError>,
  })
}

export const useActivitiesAnalysisStatusQuery = (
  params: ReadActivitiesAnalysisStatusParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readActivitiesAnalysisStatus", ...Object.values(params)],
    queryFn: () => readActivitiesAnalysisStatus(params),
    options: {
      enabled,
    } as UseQueryOptions<ReadActivitiesAnalysisStatusResponse, ApiError>,
  })
}

export const usePlayerRpeQuery = (activityId?: number) => {
  return useQueryHook({
    queryKey: ["readPlayersRpe", activityId],
    queryFn: () => readPlayersRpe(activityId as number),
    options: {
      enabled: !!activityId,
    } as UseQueryOptions<ReadPlayerRpeResponse, ApiError>,
  })
}

export const useActivityParticipatedPlayerListQuery = (
  params: ReadActivityParticipatedPlayerListParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readActivityParticipatedPlayerList", ...Object.values(params)],
    queryFn: () => readActivityParticipatedPlayerList(params),
    options: { enabled } as UseQueryOptions<ReadActivityParticipatedPlayerListResponse, ApiError>,
  })
}

export const useSessionListQuery = (params: ReadSessionListParams) => {
  return useQueryHook({
    queryKey: ["readSessionList", ...Object.values(params)],
    queryFn: () => readSessionList(params),
    options: {
      enabled: !!params.idfActivity,
    } as UseQueryOptions<Session[], ApiError>,
  })
}

export const useActivityDetailQuery = (activityId: number, enabled: boolean) => {
  return useQueryHook({
    queryKey: ["readActivityDetail", activityId],
    queryFn: () => readActivityDetail(activityId),
    options: { enabled } as UseQueryOptions<ReadActivityDetailResponse, ApiError>,
  })
}

export const useCreateActivityMutation = () => {
  return useMutationHook(createActivity, [
    ["readActivityList"],
    ["readFilteredActivities"],
    ["activityDataList"],
    ["readRecentActivityOverview"],
    ["readActivityOverviewList"],
  ])
}

export const useUpsertActivityMutation = (activityId?: number) => {
  return useMutationHook(upsertActivity, [
    ["readActivity", activityId],
    ["readActivityList"],
    ["readFilteredActivities"],
    ["activityDataList"],
    ["readRecentActivityOverview"],
    ["readActivityOverviewList"],
    ["readAnalyzingActivity"],
  ])
}

export const useDeleteActivityMutation = (activityId?: number) => {
  return useMutationHook(deleteActivity, [
    ["readActivity", activityId],
    ["readActivityList"],
    ["readFilteredActivities"],
    ["activityDataList"],
    ["readRecentActivityOverview"],
    ["readActivityOverviewList"],
  ])
}

export const useUpsertActivityTimeMutation = () => {
  return useMutationHook(upsertActivityTime)
}

export const useCreateActivityDataMutation = () => {
  return useMutationHook(createActivityData, [
    ["readActivityData"],
    ["readPlayersRpe"],
    ["readSessionList"],
    ["readAnalyzingActivity"],
  ])
}

export const useDeleteActivityDataMutation = () => {
  return useMutationHook(deleteActivityData, [
    ["readActivityData"],
    ["readPlayersRpe"],
    ["readSessionList"],
    ["readAnalyzingActivity"],
  ])
}

export const useCreateActivityAnalyzeMutation = () => {
  return useMutationHook(createActivityAnalyze, ["readAnalyzingActivity"])
}

export const useUpsertReAnalyzeActivityMutation = () => {
  return useMutationHook(upsertReAnalyzeActivity, ["readAnalyzingActivity"])
}

export const useUpsertPlayersRpeMutation = () => {
  return useMutationHook(upsertPlayersRpe, ["readPlayersRpe"])
}

export const useActivityDeviceListQuery = (idfActivity?: number) =>
  useQueryHook({
    queryKey: ["readActivityDeviceList", idfActivity],
    queryFn: () => readActivityPlayerDeviceList(idfActivity as number),
  })
